import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { ShieldCheck, CheckCircle2 } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import nurseryAccountsService from '../../services/nurseryAccounts';
import { errorMessage } from '../../utils/apiResponse';
import { useOrgTimezone, formatDateTimeInTz } from '../../utils/timezone';
import type {
  AccountRules as Rules, AccountRulesAccess, CreditAction, HoldReleaseBy, NoAbnAction, PaymentTermsType,
  SaveAccountRulesPayload,
} from '../../types/nurseryAccounts';

/**
 * /accounts/account-rules — what the organisation does about a customer with
 * no ABN, one over its credit limit or overdue, who lets a held order go, and
 * what a new customer starts on.
 *
 * Any accounts seat reads; the Accounts Admin seat changes them. The server
 * applies them (HHNodeServer nurseryAccountRulesService): at a confirmation
 * by hand, and at despatch. Nothing here works anything out.
 */

type Draft = {
  no_abn_action: NoAbnAction;
  credit_action: CreditAction;
  overdue_days: string;
  default_credit_limit: string;
  max_credit_limit: string;
  default_terms_type: PaymentTermsType;
  default_terms_days: string;
  new_customer_prepay: boolean;
  default_po_required: boolean;
  release_by: HoldReleaseBy;
  deposit_pct: string;
  deposit_min_order: string;
  deposit_due_days: string;
};

const toDraft = (r: Rules): Draft => ({
  no_abn_action: r.no_abn_action,
  credit_action: r.credit_action,
  overdue_days: String(r.overdue_days),
  default_credit_limit: r.default_credit_limit == null ? '' : String(r.default_credit_limit),
  max_credit_limit: r.max_credit_limit == null ? '' : String(r.max_credit_limit),
  default_terms_type: r.default_terms_type,
  default_terms_days: String(r.default_terms_days),
  new_customer_prepay: r.new_customer_prepay,
  default_po_required: r.default_po_required,
  release_by: r.release_by,
  deposit_pct: String(r.deposit_pct ?? 40),
  deposit_min_order: String(r.deposit_min_order ?? 0),
  deposit_due_days: String(r.deposit_due_days ?? 14),
});

const amount = (s: string): number | null | 'bad' => {
  const t = s.replace(/[$,\s]/g, '');
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) / 100 : 'bad';
};
const whole = (s: string, min: number, max: number): number | 'bad' => {
  const n = Number(s);
  return Number.isInteger(n) && n >= min && n <= max ? n : 'bad';
};

/** The payload, or the first thing wrong with the draft. */
function payloadOf(d: Draft): SaveAccountRulesPayload | string {
  const def = amount(d.default_credit_limit);
  const max = amount(d.max_credit_limit);
  const overdue = whole(d.overdue_days, 1, 365);
  const days = whole(d.default_terms_days, 0, 365);
  if (def === 'bad') return 'The default credit limit is not an amount.';
  if (max === 'bad') return 'The maximum credit limit is not an amount.';
  if (overdue === 'bad') return 'Overdue days must be a whole number from 1 to 365.';
  if (days === 'bad') return 'Payment terms must be a whole number of days from 0 to 365.';
  if (def != null && max != null && def > max) return 'The default credit limit cannot be more than the maximum.';
  const pct = Number(d.deposit_pct);
  if (!(pct > 0 && pct <= 100)) return 'The deposit is a percentage above 0 and up to 100.';
  const minOrder = amount(d.deposit_min_order);
  if (minOrder === 'bad') return 'The minimum order for a deposit is not an amount.';
  const dueDays = whole(d.deposit_due_days, 0, 365);
  if (dueDays === 'bad') return 'Days to pay a deposit must be a whole number from 0 to 365.';
  return {
    no_abn_action: d.no_abn_action,
    credit_action: d.credit_action,
    overdue_days: overdue,
    default_credit_limit: def,
    max_credit_limit: max,
    default_terms_type: d.default_terms_type,
    default_terms_days: days,
    new_customer_prepay: d.new_customer_prepay,
    default_po_required: d.default_po_required,
    release_by: d.release_by,
    deposit_pct: Math.round(pct * 100) / 100,
    deposit_min_order: minOrder ?? 0,
    deposit_due_days: dueDays,
  };
}

function Choice<T extends string>({ name, value, current, onChange, title, children }: {
  name: string; value: T; current: T; onChange: (v: T) => void; title: string; children: ReactNode;
}) {
  const id = `${name}-${value}`;
  return (
    <label htmlFor={id} className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer min-w-0 ${current === value ? 'border-primary-400 bg-primary-50' : 'border-neutral-200'}`}>
      <input id={id} type="radio" name={name} value={value} checked={current === value}
        onChange={() => onChange(value)} className="mt-1 shrink-0" />
      <span className="min-w-0 break-words">
        <span className="block text-sm font-medium text-neutral-900">{title}</span>
        <span className="block text-sm text-neutral-600">{children}</span>
      </span>
    </label>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3 min-w-0">
      <h2 className="text-base font-semibold text-neutral-900">{title}</h2>
      {children}
    </section>
  );
}

const inputCls = 'rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500 disabled:bg-neutral-50';

function AccountRules() {
  const tz = useOrgTimezone();
  const [saved, setSaved] = useState<Rules | null>(null);
  const [access, setAccess] = useState<AccountRulesAccess | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await nurseryAccountsService.getAccountRules();
      if (res.success && res.data) {
        setSaved(res.data.settings);
        setAccess(res.data.access);
        setDraft(toDraft(res.data.settings));
      } else setError(errorMessage(res, 'The account rules could not be read'));
    } catch (e) {
      setError(errorMessage(e, 'The account rules could not be read'));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setDraft((d) => (d ? { ...d, [key]: value } : d));
    setJustSaved(false);
  };

  const canEdit = !!access?.can_edit;
  const dirty = !!saved && !!draft && JSON.stringify(toDraft(saved)) !== JSON.stringify(draft);

  const save = async () => {
    if (!draft) return;
    const payload = payloadOf(draft);
    if (typeof payload === 'string') { setError(payload); return; }
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryAccountsService.saveAccountRules(payload);
      if (res.success && res.data) {
        setSaved(res.data);
        setDraft(toDraft(res.data));
        setJustSaved(true);
      } else setError(errorMessage(res, 'The account rules could not be saved'));
    } catch (e) {
      setError(errorMessage(e, 'The account rules could not be saved'));
    }
    setBusy(false);
  };

  return (
    <>
      <PageHeader
        title="Account rules"
        subtitle="No ABN, credit limits, overdue accounts and deposits"
        icon={<ShieldCheck className="h-6 w-6 text-white" />}
      />
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>}

        {access && !canEdit && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-900 break-words">
            You hold the Accounts User seat. An Accounts Admin changes the account rules.
          </div>
        )}

        {draft && (
          <fieldset disabled={!canEdit || busy} className="space-y-6 min-w-0">
            <Section title="A customer with no ABN">
              <p className="text-sm text-neutral-600 break-words">
                Judged on whoever pays: a store billed to its head office goes by the head office&apos;s ABN. An ABN is
                11 digits and an ACN 9; anything shorter, or words such as &ldquo;N/A&rdquo;, counts as none. The walk-in
                customer is never asked for one.
              </p>
              <div className="grid gap-2">
                <Choice name="no-abn" value="warn" current={draft.no_abn_action} onChange={(v) => set('no_abn_action', v)}
                  title="Warn only">
                  The order page says the customer has no ABN. Nothing is stopped.
                </Choice>
                <Choice name="no-abn" value="prepay" current={draft.no_abn_action} onChange={(v) => set('no_abn_action', v)}
                  title="Pay before despatch">
                  The customer has no trade account. Its orders can be entered, picked and loaded, but the load is not
                  despatched until the order is paid or released.
                </Choice>
              </div>
            </Section>

            <Section title="Over the credit limit, or overdue">
              <p className="text-sm text-neutral-600 break-words">
                An order is over the limit when what the payer owes on invoices plus this order, GST included, comes to
                more than its limit. Overdue is anything owed more than the days below past its due date. An order that
                is already paid is never held for credit.
              </p>
              <div className="grid gap-2">
                <Choice name="credit" value="warn" current={draft.credit_action} onChange={(v) => set('credit_action', v)}
                  title="Warn only">
                  The order page says so. The order can still go ahead.
                </Choice>
                <Choice name="credit" value="hold" current={draft.credit_action} onChange={(v) => set('credit_action', v)}
                  title="Hold at despatch">
                  The order can be confirmed and picked, but its load is not despatched until it is paid or released.
                </Choice>
                <Choice name="credit" value="refuse" current={draft.credit_action} onChange={(v) => set('credit_action', v)}
                  title="Refuse to confirm">
                  A new order cannot be confirmed by hand until it is released. Orders that confirm themselves (website,
                  imported purchase orders, backorders) are held at despatch instead.
                </Choice>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1 min-w-0">
                  <label htmlFor="overdue-days" className="block text-sm font-medium text-neutral-800">Overdue after (days)</label>
                  <input id="overdue-days" type="text" inputMode="numeric" value={draft.overdue_days}
                    onChange={(e) => set('overdue_days', e.target.value)} className={`${inputCls} w-full`} />
                </div>
                <div className="space-y-1 min-w-0">
                  <label htmlFor="default-limit" className="block text-sm font-medium text-neutral-800">Default credit limit</label>
                  <input id="default-limit" type="text" inputMode="decimal" placeholder="None" value={draft.default_credit_limit}
                    onChange={(e) => set('default_credit_limit', e.target.value)} className={`${inputCls} w-full`} />
                  <p className="text-xs text-neutral-500 break-words">For a customer with no limit of its own.</p>
                </div>
                <div className="space-y-1 min-w-0">
                  <label htmlFor="max-limit" className="block text-sm font-medium text-neutral-800">Maximum credit limit</label>
                  <input id="max-limit" type="text" inputMode="decimal" placeholder="None" value={draft.max_credit_limit}
                    onChange={(e) => set('max_credit_limit', e.target.value)} className={`${inputCls} w-full`} />
                  <p className="text-xs text-neutral-500 break-words">Only an Accounts Admin can set a customer above it.</p>
                </div>
              </div>
            </Section>

            <Section title="Who lets a held order go">
              <p className="text-sm text-neutral-600 break-words">
                A held order is released on its page, with a note: a payment reference, or who agreed it. The release
                stands until the order is despatched.
              </p>
              <div className="grid gap-2">
                <Choice name="release" value="accounts_admin" current={draft.release_by} onChange={(v) => set('release_by', v)}
                  title="An Accounts Admin">
                  Only someone holding the Accounts Admin seat.
                </Choice>
                <Choice name="release" value="org_admin_too" current={draft.release_by} onChange={(v) => set('release_by', v)}
                  title="An Accounts Admin or an organisation admin">
                  Also anyone with the admin role in this organisation, without an accounts seat.
                </Choice>
              </div>
            </Section>

            <Section title="Deposits">
              <p className="text-sm text-neutral-600 break-words">
                A customer set to pay deposits is asked for one on each order for trees not ready yet: this percentage
                of the trees before GST (unless the customer has its own), once the order comes to at least the minimum.
                The deposit invoice goes to Xero, and it counts as paid only once it is reconciled there. Each invoice
                for that customer then takes off the deposits paid, oldest first.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1 min-w-0">
                  <label htmlFor="deposit-pct" className="block text-sm font-medium text-neutral-800">Deposit (% of trees ex GST)</label>
                  <input id="deposit-pct" type="text" inputMode="decimal" value={draft.deposit_pct}
                    onChange={(e) => set('deposit_pct', e.target.value)} className={`${inputCls} w-full`} />
                </div>
                <div className="space-y-1 min-w-0">
                  <label htmlFor="deposit-min" className="block text-sm font-medium text-neutral-800">Only on orders from</label>
                  <input id="deposit-min" type="text" inputMode="decimal" value={draft.deposit_min_order}
                    onChange={(e) => set('deposit_min_order', e.target.value)} className={`${inputCls} w-full`} />
                  <p className="text-xs text-neutral-500 break-words">Trees ex GST. 0 asks on every order.</p>
                </div>
                <div className="space-y-1 min-w-0">
                  <label htmlFor="deposit-days" className="block text-sm font-medium text-neutral-800">Days to pay</label>
                  <input id="deposit-days" type="text" inputMode="numeric" value={draft.deposit_due_days}
                    onChange={(e) => set('deposit_due_days', e.target.value)} className={`${inputCls} w-full`} />
                </div>
              </div>
            </Section>

            <Section title="What a new customer starts on">
              <p className="text-sm text-neutral-600 break-words">
                Terms and PO required apply to any customer that does not set its own, now and later. Pay before
                despatch is ticked on each customer added from now on, until an Accounts Admin takes it off.
              </p>
              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1">
                  <label htmlFor="terms-days" className="block text-sm font-medium text-neutral-800">Payment terms (days)</label>
                  <input id="terms-days" type="text" inputMode="numeric" value={draft.default_terms_days}
                    onChange={(e) => set('default_terms_days', e.target.value)} className={`${inputCls} w-24`} />
                </div>
                <div className="space-y-1">
                  <label htmlFor="terms-type" className="block text-sm font-medium text-neutral-800">Counted from</label>
                  <select id="terms-type" value={draft.default_terms_type}
                    onChange={(e) => set('default_terms_type', e.target.value as PaymentTermsType)} className={inputCls}>
                    <option value="days">the invoice date</option>
                    <option value="eom_days">the end of the invoice&apos;s month</option>
                  </select>
                </div>
              </div>
              <label htmlFor="po-required" className="flex items-start gap-2 text-sm text-neutral-800 break-words">
                <input id="po-required" type="checkbox" checked={draft.default_po_required}
                  onChange={(e) => set('default_po_required', e.target.checked)} className="mt-0.5 shrink-0" />
                <span>A purchase order number is required on orders</span>
              </label>
              <label htmlFor="new-prepay" className="flex items-start gap-2 text-sm text-neutral-800 break-words">
                <input id="new-prepay" type="checkbox" checked={draft.new_customer_prepay}
                  onChange={(e) => set('new_customer_prepay', e.target.checked)} className="mt-0.5 shrink-0" />
                <span>New customers pay before despatch until they are given an account</span>
              </label>
            </Section>

            {canEdit && (
              <div className="flex flex-wrap items-center gap-3">
                <Button onClick={save} disabled={!dirty || busy}>{busy ? 'Saving…' : 'Save'}</Button>
                {justSaved && (
                  <span className="inline-flex items-center gap-1 text-sm text-green-700">
                    <CheckCircle2 className="h-4 w-4" /> Saved
                  </span>
                )}
              </div>
            )}
            {saved?.updated_at && (
              <p className="text-xs text-neutral-500">Last changed {formatDateTimeInTz(saved.updated_at, tz)}</p>
            )}
          </fieldset>
        )}
      </div>
    </>
  );
}

export default AccountRules;
